import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GridLegacy as Grid,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';

import { getRegionCodes } from '@luxuryescapes/lib-regions';

import PromoCodeNameField from '~/components/Promo/PromoCodeNameField';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import {
  createReferralEarnOption,
  createReferralEarnOptionPayload,
  updateReferralEarnOption,
  updateReferralEarnOptionPayload,
} from '~/services/PromoService';

import {
  EarnOption,
  EarnOptionBrand,
  EarnOptionRegion,
  EarnOptionType,
  EarnOptionTypeCode,
  ReferralEarnStep,
  earnTypeIsCredit,
  earnTypeIsPromoCode,
} from './utils';

interface Props {
  closeModal: () => void;
  fetchData: () => void;
  activeEarnOption?: EarnOption;
}

const DEFAULT_EXPIRY_MONTHS = 12;

function ReferralEarnOptionForm({ closeModal, fetchData, activeEarnOption }: Props) {
  const [region, setRegion] = useState<EarnOptionRegion | null>(activeEarnOption?.region);
  const [name, setName] = useState<string>(activeEarnOption?.name);
  const [summary, setSummary] = useState<string>(activeEarnOption?.summary);
  const [displayName, setDisplayName] = useState<string>(activeEarnOption?.display_name);
  const [referralEarnTypeCode, setReferralEarnTypeCode] = useState<EarnOptionTypeCode | null>(
    activeEarnOption?.earn_type?.earn_type_code,
  );
  const [referralEarnType, setReferralEarnType] = useState<EarnOptionType | null>(activeEarnOption?.earn_type);
  const [referralEarnStep, setReferralEarnStep] = useState<ReferralEarnStep | null>(activeEarnOption?.earn_step);
  const { tenant } = useCurrentTenant();
  const { enqueueSnackbar } = useSnackbar();

  const handleReferralEarnTypeChange = (value: EarnOptionTypeCode) => {
    setReferralEarnTypeCode(value);
    if (value === 'credit') {
      setReferralEarnType({
        earn_type_code: value,
        kind: 'PaymentService',
        earn_model: {
          value: 50,
          kind: 'FlatModel',
        },
        creditExpiresMonths: DEFAULT_EXPIRY_MONTHS,
      });
    } else if (value === 'promo_code') {
      setReferralEarnType({
        earn_type_code: value,
        code_name: '',
        promo_expires_months: DEFAULT_EXPIRY_MONTHS,
      });
    } else {
      setReferralEarnType(null);
    }
  };

  const handleCreate = async () => {
    const payload: createReferralEarnOptionPayload = {
      region: region,
      name: name,
      summary: summary,
      earn_type: referralEarnType,
      earn_priority: 0,
      earn_step: referralEarnStep,
      brand: tenant.brand as EarnOptionBrand,
      display_name: displayName,
    };
    const response = await createReferralEarnOption(payload);
    if (response.status === 201) {
      reset();
      closeModal();
      enqueueSnackbar('Referral Earn Option created successfully', {
        variant: 'success',
      });
      fetchData();
    } else {
      enqueueSnackbar('Error creating referral earn option', {
        variant: 'error',
      });
    }
  };

  const handleUpdate = async () => {
    const payload: updateReferralEarnOptionPayload['earn_option'] = {
      earn_option_id: activeEarnOption?.earn_option_id,
      display_name: displayName,
      name,
      summary,
      earn_step: referralEarnStep,
      earn_priority: 0,
    };
    const response = await updateReferralEarnOption({ earn_option: payload });
    if (response.status === 200) {
      reset();
      closeModal();
      enqueueSnackbar('Referral Earn Option updated successfully', {
        variant: 'success',
      });
      fetchData();
    } else {
      enqueueSnackbar('Error updating referral earn option', {
        variant: 'error',
      });
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Record<string, unknown>>();

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open
      onClose={closeModal}
      component="form"
      onSubmit={handleSubmit(activeEarnOption ? handleUpdate : handleCreate)}
    >
      <DialogTitle sx={{ m: 0, p: 2 }} id="modal-title">
        {activeEarnOption ? 'Update' : 'Create'} a Referral Earn Option
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={1}>
          <Grid container item spacing={1}>
            <Grid item xs={6}>
              <Stack spacing={2}>
                <Autocomplete
                  id="region"
                  options={getRegionCodes()}
                  value={region}
                  disabled={!!activeEarnOption}
                  onChange={(_, newValue) => {
                    setRegion(newValue as EarnOptionRegion);
                  }}
                  renderInput={(params) => <TextField {...params} label="Region" />}
                />
                <Autocomplete
                  id="referral_earn_type"
                  options={['credit', 'promo_code']}
                  value={referralEarnTypeCode}
                  onChange={(_, newValue) => {
                    handleReferralEarnTypeChange(newValue as EarnOptionTypeCode);
                  }}
                  disabled={!!activeEarnOption}
                  renderInput={(params) => <TextField {...params} label="Referral Earn Type" />}
                />
                <Autocomplete
                  id="earn_step"
                  options={['signup', 'booking', 'post_travel']}
                  value={referralEarnStep}
                  onChange={(_, newValue) => {
                    setReferralEarnStep(newValue as ReferralEarnStep);
                  }}
                  renderInput={(params) => <TextField {...params} label="Referral Earn Step" />}
                />
              </Stack>
            </Grid>
            <Grid item xs={6}>
              <Stack spacing={2}>
                <TextField
                  id="summary"
                  label="Summary"
                  variant="outlined"
                  value={summary}
                  error={!!errors.summary}
                  helperText={errors.summary?.message}
                  autoComplete="off"
                  onChange={(e) => setSummary(e.target.value)}
                />
                <TextField
                  id="name"
                  label="Name"
                  variant="outlined"
                  value={name}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  autoComplete="off"
                  onChange={(e) => setName(e.target.value)}
                />
                <Tooltip title="Set to 0 or leave blank for unlimited redemptions.">
                  <TextField
                    id="redemptionLimitPerStepPerUser"
                    label="Redemption Limit Per Step Per User Per Year"
                    variant="outlined"
                    value={referralEarnType?.redeem_limit_per_step_per_user_per_year}
                    autoComplete="off"
                    onChange={(e) =>
                      setReferralEarnType({
                        ...referralEarnType,
                        redeem_limit_per_step_per_user_per_year: Number(e.target.value),
                      })
                    }
                    disabled={!!activeEarnOption}
                    type="number"
                  />
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
          <Grid container item spacing={1}>
            <Grid item xs={12}>
              <Stack spacing={2}>
                <TextField
                  id="display_name"
                  label="Display Name"
                  variant="outlined"
                  {...register('display_name', {
                    required: 'Display Name is required',
                  })}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  error={!!errors.header_text}
                  helperText={errors.header_text?.message}
                  autoComplete="off"
                  multiline
                  rows={4}
                />
              </Stack>
            </Grid>
          </Grid>
          {referralEarnTypeCode && earnTypeIsCredit(referralEarnType) && (
            <Grid container spacing={1}>
              <Grid container item xs={12}>
                <DialogTitle sx={{ m: 0, p: 2 }} id="sub-title">
                  Credit Details
                </DialogTitle>
              </Grid>
              <Grid container item xs={6}>
                <TextField
                  id="value"
                  label="Credit Value"
                  variant="outlined"
                  value={referralEarnType?.earn_model.value}
                  autoComplete="off"
                  disabled={!!activeEarnOption}
                  onChange={(e) =>
                    setReferralEarnType({
                      ...referralEarnType,
                      earn_model: { ...referralEarnType?.earn_model, value: Number(e.target.value) },
                    })
                  }
                  type="number"
                  fullWidth
                />
              </Grid>
              <Grid container item xs={6}>
                <TextField
                  id="creditExpiresMonths"
                  label="Credit Expires Months"
                  variant="outlined"
                  value={referralEarnType?.creditExpiresMonths}
                  autoComplete="off"
                  disabled={!!activeEarnOption}
                  onChange={(e) =>
                    setReferralEarnType({
                      ...referralEarnType,
                      creditExpiresMonths: Number(e.target.value),
                    })
                  }
                  type="number"
                  fullWidth
                />
              </Grid>
            </Grid>
          )}
          {referralEarnTypeCode && earnTypeIsPromoCode(referralEarnType) && (
            <Grid container spacing={1}>
              <Grid container item xs={12}>
                <DialogTitle sx={{ m: 0, p: 2 }} id="sub-title">
                  Credit Details
                </DialogTitle>
              </Grid>
              <Grid container item xs={6}>
                <PromoCodeNameField
                  id="code_name"
                  label="Promo Code Name"
                  variant="outlined"
                  value={referralEarnType?.code_name}
                  autoComplete="off"
                  onChange={(e) =>
                    setReferralEarnType({
                      ...referralEarnType,
                      code_name: e.target.value,
                    })
                  }
                  fullWidth
                />
              </Grid>
              <Grid container item xs={6}>
                <TextField
                  id="promo_expires_months"
                  label="Promo Expires Months"
                  variant="outlined"
                  value={referralEarnType?.promo_expires_months}
                  autoComplete="off"
                  onChange={(e) =>
                    setReferralEarnType({
                      ...referralEarnType,
                      promo_expires_months: Number(e.target.value),
                    })
                  }
                  type="number"
                  fullWidth
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      {referralEarnTypeCode && (
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button variant="contained" type="submit">
            {activeEarnOption ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default ReferralEarnOptionForm;
